import React, { useEffect, useState, useCallback } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Shield, Lock, RefreshCw, Download, CheckCircle2, AlertTriangle, Archive,
} from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function short(hash) {
  if (!hash) return "—";
  return `${hash.slice(0, 8)}…${hash.slice(-8)}`;
}

export default function Compliance() {
  const companyId = localStorage.getItem("demoCompanyId");
  const [locks, setLocks] = useState([]);
  const [verify, setVerify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [periodStart, setPeriodStart] = useState(`${new Date().getFullYear() - 1}-01-01`);
  const [periodEnd, setPeriodEnd] = useState(`${new Date().getFullYear() - 1}-12-31`);
  const [exportYear, setExportYear] = useState(new Date().getFullYear() - 1);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/compliance/locks`);
      setLocks(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Locks nicht laden");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleLock = async () => {
    if (!periodStart || !periodEnd) return;
    if (!window.confirm(
      `Periode ${periodStart} bis ${periodEnd} GeBüV-konform festschreiben?\n\n`
      + `Alle Buchungen in diesem Zeitraum werden unwiderruflich archiviert und können `
      + `danach nicht mehr gelöscht oder geändert werden. Korrekturen sind nur über `
      + `Storno-Buchungen möglich.`
    )) return;
    setLoading(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/compliance/lock-period`,
        { period_start: periodStart, period_end: periodEnd },
      );
      toast.success(`${r.data.entry_count} Buchung(en) archiviert · Hash ${short(r.data.lock_hash)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lock fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/compliance/verify-chain`);
      setVerify(r.data);
      if (r.data.ok) {
        toast.success(`Hash-Kette OK · ${r.data.entries_total} Einträge in ${r.data.periods_total} Periode(n)`);
      } else {
        toast.error(`${r.data.mismatches.length} Manipulations-Verdacht`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verifikation fehlgeschlagen");
    } finally {
      setVerifying(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/compliance/archive-export?year=${exportYear}`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `archive_${exportYear}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archiv-ZIP heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Shield className="h-7 w-7 text-emerald-600" />
          Compliance &amp; GeBüV-Archiv
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Periodenabschluss mit kryptographischer Hash-Kette nach Art. 957a OR + GeBüV. 10-Jahres-Aufbewahrung.
        </p>
      </div>

      {/* Period-Lock */}
      <Card className="p-5" data-testid="compliance-lock-card">
        <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Lock className="h-4 w-4 text-amber-600" />
          Periode festschreiben
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Schreibt alle Buchungen im gewählten Zeitraum unwiderruflich fest. Jeder Eintrag erhält
          einen SHA-256-Hash, der mit dem vorherigen Lock verkettet ist. Manipulationen werden
          beim Verify sofort sichtbar.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Periode von</label>
            <SwissDateField
              
              value={periodStart}
              max={today}
              onChange={(e) => setPeriodStart(e.target.value)}
              data-testid="compliance-period-start"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Periode bis</label>
            <SwissDateField
              
              value={periodEnd}
              max={today}
              onChange={(e) => setPeriodEnd(e.target.value)}
              data-testid="compliance-period-end"
            />
          </div>
          <Button
            onClick={handleLock}
            disabled={loading || !periodStart || !periodEnd}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="compliance-lock-btn"
          >
            <Lock className="h-4 w-4 mr-1.5" />
            Periode festschreiben
          </Button>
        </div>
      </Card>

      {/* Locks-Tabelle */}
      <Card className="p-5" data-testid="compliance-locks-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Archive className="h-4 w-4 text-slate-700" />
            Archivierte Perioden
            <Badge variant="secondary">{locks.length}</Badge>
          </h2>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="compliance-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
        {locks.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="compliance-locks-empty">
            Noch keine festgeschriebenen Perioden. Schreibe deine erste Periode oben fest.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compliance-locks-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2">Periode</th>
                  <th className="py-2 px-2">Festgeschrieben</th>
                  <th className="py-2 px-2 text-right">Einträge</th>
                  <th className="py-2 px-2">Hash</th>
                  <th className="py-2 px-2">Vorgänger</th>
                  <th className="py-2 px-2">Von</th>
                </tr>
              </thead>
              <tbody>
                {locks.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100" data-testid={`compliance-lock-row-${l.id}`}>
                    <td className="py-2 px-2 font-medium text-slate-900">
                      {l.period_start} – {l.period_end}
                    </td>
                    <td className="py-2 px-2 text-slate-600">{fmtDateTime(l.locked_at)}</td>
                    <td className="py-2 px-2 text-right font-mono">{l.entry_count}</td>
                    <td className="py-2 px-2 font-mono text-xs text-slate-700" title={l.lock_hash}>{short(l.lock_hash)}</td>
                    <td className="py-2 px-2 font-mono text-xs text-slate-500" title={l.prev_lock_hash || "—"}>
                      {l.prev_lock_hash ? short(l.prev_lock_hash) : <span className="text-emerald-600">Genesis</span>}
                    </td>
                    <td className="py-2 px-2 text-xs text-slate-500">{l.locked_by_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Verify + Export Row */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-5" data-testid="compliance-verify-card">
          <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Hash-Kette verifizieren
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Berechnet alle Hashes neu und vergleicht sie mit den gespeicherten Werten.
            Mismatches sind Manipulationen.
          </p>
          <Button onClick={handleVerify} disabled={verifying} variant="outline" data-testid="compliance-verify-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${verifying ? "animate-spin" : ""}`} />
            Jetzt verifizieren
          </Button>
          {verify && (
            <div className={`mt-4 p-3 rounded-md border ${verify.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="compliance-verify-result">
              <div className="flex items-center gap-2 text-sm font-medium">
                {verify.ok ? (
                  <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Kette intakt</>
                ) : (
                  <><AlertTriangle className="h-4 w-4 text-rose-600" /> {verify.mismatches.length} Manipulation(en)</>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {verify.periods_ok}/{verify.periods_total} Perioden · {verify.entries_ok}/{verify.entries_total} Einträge
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="compliance-export-card">
          <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <Download className="h-4 w-4 text-sky-600" />
            Archiv-Export (ZIP)
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Lädt alle archivierten Buchungen eines Jahres als ZIP herunter — inkl.
            <code className="bg-slate-100 px-1 rounded mx-1">manifest.json</code>,
            <code className="bg-slate-100 px-1 rounded mx-1">journal.jsonl</code> und
            <code className="bg-slate-100 px-1 rounded mx-1">journal.csv</code>.
            Aufbewahrungspflicht: 10 Jahre.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600 block mb-1">Jahr</label>
              <Input
                type="number"
                min="2000" max="2099"
                value={exportYear}
                onChange={(e) => setExportYear(parseInt(e.target.value, 10) || exportYear)}
                data-testid="compliance-export-year"
              />
            </div>
            <Button onClick={handleExport} disabled={exporting} variant="outline" data-testid="compliance-export-btn">
              <Download className={`h-4 w-4 mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
              ZIP herunterladen
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
